import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

// Constants
import ROUTE from '../../constants/route-constants';
import URL from '../../constants/api-constants';

// Http
import http from '../../store/http/http-common';

// Components
import IconGridView from '../Icons/IconGridView/IconGridView';
import IconHeatViewControls from '../Icons/IconHeatViewControls/IconHeatViewControls';
import IconLeftArrow from '../Icons/IconLeftArrow/IconLeftArrow';
import IconLocation from '../Icons/IconLocation/IconLocation';
import IconTeam from '../Icons/IconTeam/IconTeam';
import IconWifi from '../Icons/IconWifi/IconWifi';
import IconSuitCase from '../Icons/IconSuitCase/IconSuitCase';

import HeatViewDefault from '../Icons/HeatViewDefault/HeatviewDefault';

import { AbilityContext } from '../Can/Can';
import { Action } from '../../enums/action';
import { PAGE_TITLE } from '../../constants/common';
import IconContract from '../Icons/IconContract/IconContract';
import { EnvType } from '../../enums/common';
import { IconRecipe } from '../Icons/IconRecipe';
import { getYear } from 'date-fns';

type SidebarProps = {
  sidebarOpen: boolean;
  logo: string | null;
  isUserLoaded: boolean;
  setSidebarOpen: (arg: boolean) => void;
  isCustomer: boolean;
};

const Sidebar: React.FC<SidebarProps> = ({ sidebarOpen, setSidebarOpen, logo, isUserLoaded, isCustomer }) => {
  const VITE_APP_SENTRY_NODE_ENV = import.meta.env.VITE_APP_SENTRY_NODE_ENV;

  const LOCAL_STORAGE_ITEMS = {
    SIDEBAR_EXPANDED: 'sidebar-expanded',
  };

  const navigate = useNavigate();
  const location = useLocation();
  const ability = useContext(AbilityContext);
  const { pathname } = location;

  const [backendVersion, setBackendVersion] = useState<string | null>(null);

  useEffect(() => {
    http
      .get<{ version: string }>(URL.APP_VERSION)
      .then((response) => setBackendVersion(response.data.version))
      .catch(() => setBackendVersion(null));
  }, []);

  const ROUTES = {
    CONTRACTS: [ROUTE.CONTRACTS, ROUTE.EDIT_CONTRACT, ROUTE.CREATE_CONTRACT],
  };

  const canSeeSomeOrgFields = useMemo(() => {
    return (
      ability.can(Action.Read, PAGE_TITLE.ORGANIZATIONS) ||
      ability.can(Action.Read, PAGE_TITLE.MEMBERS) ||
      ability.can(Action.Read, PAGE_TITLE.CONTROLLERS) ||
      ability.can(Action.Read, PAGE_TITLE.RECIPES)
    );
  }, [ability]);

  const canSeeSomeCustomersFields = useMemo(() => {
    return ability.can(Action.Read, PAGE_TITLE.LOCATIONS) || ability.can(Action.Read, PAGE_TITLE.CONTRACTS);
  }, [ability]);

  const trigger = useRef<any>(null);
  const sidebar = useRef<any>(null);

  const getIsSideBarExtended = (): boolean => {
    const DEFAULT_STATE = false; // depends on a specific UI requirements
    const stateFromStorage = localStorage.getItem(LOCAL_STORAGE_ITEMS.SIDEBAR_EXPANDED);

    if (typeof stateFromStorage === 'string') {
      return Boolean(stateFromStorage);
    } else {
      return DEFAULT_STATE;
    }
  };

  const sidebarExpanded = getIsSideBarExtended();

  const matchRoute = (pathname: string, routes: string[]): boolean => {
    const patterns = routes.map((route) => new RegExp(`^${route.replace(/:\w+/g, '[^/]+')}$`));

    return patterns.some((pattern) => pattern.test(pathname));
  };

  const moveToHome = () => {
    if (ability.can(Action.Read, PAGE_TITLE.HOME)) {
      navigate(ROUTE.HOME);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const createSentryError = () => {
    if (VITE_APP_SENTRY_NODE_ENV === EnvType.PROD) {
      return;
    }

    throw new Error('This is test sentry error!');
  };

  const date = new Date();

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!sidebar.current || !trigger.current) return;
      if (!sidebarOpen || sidebar.current.contains(target) || trigger.current.contains(target)) return;
      setSidebarOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_ITEMS.SIDEBAR_EXPANDED, sidebarExpanded.toString());
    if (sidebarExpanded) {
      document.querySelector('body')?.classList.add(LOCAL_STORAGE_ITEMS.SIDEBAR_EXPANDED);
    } else {
      document.querySelector('body')?.classList.remove(LOCAL_STORAGE_ITEMS.SIDEBAR_EXPANDED);
    }
  }, [sidebarExpanded]);

  return (
    <aside
      ref={sidebar}
      className={`c-sidebar absolute left-0 top-0 z-9999 flex h-screen w-full flex-col overflow-y-hidden bg-black-3 px-4 pt-10 duration-300 ease-linear dark:bg-boxdark xsm:w-72.5 lg:static lg:translate-x-0 lg:px-6 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}
    >
      <div className="c-sidebar__header flex items-center justify-between pb-11 lg:justify-start">
        {isUserLoaded && (
          <>
            {!logo && (
              <div className="pl-4" onClick={moveToHome}>
                <HeatViewDefault />
              </div>
            )}
            {logo && <img className="max-h-22" src={logo} onClick={moveToHome} alt="logo" />}
          </>
        )}

        <button
          ref={trigger}
          onClick={toggleSidebar}
          aria-controls="sidebar"
          aria-expanded={sidebarOpen}
          className="block lg:hidden"
        >
          <IconLeftArrow fill="#EF8541" />
        </button>
      </div>
      <div className="c-sidebar__content  flex flex-auto flex-col justify-between overflow-y-auto pb-10 duration-300 ease-linear">
        <nav className="c-sidebar__navigation">
          {ability.can(Action.Read, PAGE_TITLE.HOME) && (
            <div className="c-sidebar__menu">
              <h3 className="mb-4 ml-4 text-sm font-semibold text-zinc-500">MENU</h3>
              <ul className="mb-6 flex flex-col gap-1.5">
                <li>
                  <NavLink
                    to="/"
                    className={`group relative flex items-center gap-2.5 rounded-sm px-4 py-2 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-charcoal-gray dark:hover:bg-meta-4 ${
                      (pathname === '/' || pathname.includes('dashboard') || isCustomer) &&
                      'bg-charcoal-gray dark:bg-meta-4'
                    }`}
                  >
                    <IconGridView fill="#EF8541" iconClassName="w-[18px] h-5" />
                    Dashboard
                  </NavLink>
                </li>
              </ul>
            </div>
          )}
          {canSeeSomeOrgFields && (
            <div className="c-sidebar__organizations">
              <h3 className="mb-4 ml-4 text-sm font-semibold text-zinc-500">ORGANIZATION</h3>
              <ul className="mb-6 flex flex-col gap-1.5">
                {ability.can(Action.Read, PAGE_TITLE.ORGANIZATIONS) && (
                  <li>
                    <NavLink
                      to={ROUTE.ORGANIZATIONS}
                      className={`group relative flex items-center gap-2.5 rounded-sm px-4 py-2 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-charcoal-gray dark:hover:bg-meta-4 ${
                        pathname.includes('organizations') && 'bg-charcoal-gray dark:bg-meta-4'
                      } `}
                    >
                      <IconSuitCase fill="#EF8541" width={18} height={16} iconClassName="w-[18px] h-5" />
                      Organization
                    </NavLink>
                  </li>
                )}
                {ability.can(Action.Read, PAGE_TITLE.MEMBERS) && (
                  <li>
                    <NavLink
                      to={ROUTE.MEMBERS}
                      className={`group relative flex items-center gap-2.5 rounded-sm px-4 py-2 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-charcoal-gray dark:hover:bg-meta-4 ${
                        pathname.includes('members') && 'bg-charcoal-gray dark:bg-meta-4'
                      } `}
                    >
                      <IconTeam fill="#EF8541" iconClassName="w-[18px] h-5" />
                      Members
                    </NavLink>
                  </li>
                )}
                {ability.can(Action.Read, PAGE_TITLE.CONTROLLERS) && (
                  <li>
                    <NavLink
                      to={ROUTE.CONTROLLERS}
                      className={`group relative flex items-center gap-2.5 rounded-sm px-4 py-2 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-charcoal-gray dark:hover:bg-meta-4 ${
                        pathname.includes('controllers') && 'bg-charcoal-gray dark:bg-meta-4'
                      } `}
                    >
                      <IconWifi fill="#EF8541" iconClassName="w-[18px] h-5" />
                      Controllers
                    </NavLink>
                  </li>
                )}
                {ability.can(Action.Read, PAGE_TITLE.RECIPES) && (
                  <li>
                    <NavLink
                      to={ROUTE.RECIPES}
                      className={`group relative flex items-center gap-2.5 rounded-sm px-4 py-2 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-charcoal-gray dark:hover:bg-meta-4 ${
                        pathname.includes(PAGE_TITLE.RECIPES.toLowerCase()) && 'bg-charcoal-gray dark:bg-meta-4'
                      } `}
                    >
                      <IconRecipe stroke="#EF8541" iconClassName="w-[18px] h-5" />
                      Recipes
                    </NavLink>
                  </li>
                )}
              </ul>
            </div>
          )}
          {canSeeSomeCustomersFields && (
            <div className="c-sidebar__customers">
              <h3 className="mb-4 ml-4 text-sm font-semibold text-zinc-500">CUSTOMERS</h3>
              <ul className="mb-6 flex flex-col gap-1.5">
                {ability.can(Action.Read, PAGE_TITLE.LOCATIONS) && (
                  <li>
                    <NavLink
                      to={ROUTE.LOCATIONS}
                      className={`group relative flex items-center gap-2.5 rounded-sm px-4 py-2 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-charcoal-gray dark:hover:bg-meta-4 ${
                        pathname.includes('clients') && 'bg-charcoal-gray dark:bg-meta-4'
                      } `}
                    >
                      <IconLocation fill="#EF8541" height={20} iconClassName="w-[18px] h-5" />
                      Clients
                    </NavLink>
                  </li>
                )}
                {ability.can(Action.Read, PAGE_TITLE.CONTRACTS) && (
                  <li>
                    <NavLink
                      to={ROUTE.CONTRACTS}
                      className={`group relative flex items-center gap-2.5 rounded-sm px-4 py-2 font-medium text-bodydark1 duration-300 ease-in-out hover:bg-charcoal-gray dark:hover:bg-meta-4 ${
                        matchRoute(pathname, ROUTES.CONTRACTS) && 'bg-charcoal-gray dark:bg-meta-4'
                      } `}
                    >
                      <IconContract fill="#EF8541" iconClassName="w-[18px] h-5" />
                      Contracts
                    </NavLink>
                  </li>
                )}
              </ul>
            </div>
          )}
        </nav>
        <div className="c-sidebar__footer flex flex-col items-start self-end pl-3 text-slate-gray">
          {isUserLoaded && logo && <IconHeatViewControls iconClassName="pb-3" fill="#818181" />}
          <p>
            Powered by&nbsp;
            <a href="https://heatviewcontrols.com/" target="_blank" className="text-primary" rel="noreferrer">
              Heat View Controls
            </a>
          </p>
          <p>
            COPYRIGHT © {getYear(date)}. All rights reserved to Heat View Controls
            <span onClick={createSentryError}>.</span>
          </p>
          <p>
            <sub>Frontend v1.15.9</sub>
          </p>
          <p>
            <sub>Backend {backendVersion ? `v${backendVersion}` : '—'}</sub>
          </p>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
